import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import set from "lodash/set";
import i18n from "../i18n";
import { youtubeParser } from "../utils/video-playlist";
import {
  isLibraryMetadataFromAddExercise,
  respondLibraryMetadataFromAddExercise,
} from "./respond-library-metadata-from-add-exercise";

const otherExerciseProperties = async (
  body,
  files,
  api,
  assert,
  user,
  exercise = null
) => {
  const {
    exerciseTypeField,
    primaryMuscleGroup,
    secondaryMuscleGroup,
    instructions,
    videoUrl,
    videoDeleteOtherExercise,
  } = body;

  let videoData;
  if (!videoUrl && !isEmpty(files)) {
    videoData = await (
      await api("other-exercise-video", {
        method: "POST",
        body: { userId: user._id, userEmail: user.email },
        files,
      })
    ).json();
  } else if (videoUrl) {
    videoData = {
      url: videoUrl,
    };
  }
  return {
    video: videoData || (videoDeleteOtherExercise ? "" : exercise?.video || ""), // video empty it means deleted
    exerciseTypeField: exerciseTypeField === "None" ? "" : exerciseTypeField,
    primaryMuscleGroup: primaryMuscleGroup === "None" ? "" : primaryMuscleGroup,
    secondaryMuscleGroup:
      secondaryMuscleGroup === "None" ? "" : secondaryMuscleGroup,
    instructions: instructions ? instructions.trim() : "",
  };
};

/**if the user doesn't have that value in the exercise library, we are going to create the property with
 * an empty array to push the exercise there, this is for weird cases where the users doesnt have that exercise.primary
 * value in the exercise library. EXAMPLE:
 * user.exerciseLibrary = {squat: ..., assistant: ..., deadlift: ...] here doesn't exists "bench"
 * So if an user tries to add a bench variaty in the exercise library this is going to fail because we are pushing an
 * exercise in a undefined value instead in an array.
 **/
const validateEmptyExerciseLibrary = (exerciseLibrary, primary) => {
  if (
    !exerciseLibrary[primary] &&
    [
      "assistant",
      "squat",
      "bench",
      "deadlift",
      "clean",
      "jerk",
      "snatch",
      "overheadPress",
      "other",
    ].includes(primary)
  ) {
    exerciseLibrary[primary] = [];
  }
};

const validateEmptyRM = (repMaximums) => {
  if (!repMaximums?.clean) repMaximums.clean = 100;
  if (!repMaximums?.jerk) repMaximums.jerk = 100;
  if (!repMaximums?.snatch) repMaximums.snatch = 100;
  if (!repMaximums?.overheadPress) repMaximums.overheadPress = 100;
  if (!repMaximums?.other) repMaximums.other = 100;
};

const updatePrimaryRM = (body, user, repMaximum) => {
  const bodyRM = {};
  const { exerciseLibrary } = user;

  bodyRM.action = "updateUserPreferences";
  bodyRM.repMaximums = user.repMaximums;
  bodyRM.repMaximums[body.primary] = repMaximum;
  bodyRM.exerciseLibrary = user.exerciseLibrary;
  bodyRM.primaryExercises = user.primaryExercises;
  //we are being sure that object existis in the exercise library array
  validateEmptyExerciseLibrary(exerciseLibrary, body.primary);
  validateEmptyRM(bodyRM.repMaximums);
  exerciseLibrary[body.primary].forEach((movement) => {
    if (!movement.customizedRM) {
      movement.repMaximum = bodyRM.repMaximums[body.primary];
    }
  });

  return bodyRM;
};

const updateSecondaryRM = (body, athlete) => {
  const bodyRM = {};
  let change = false;

  bodyRM.action = "updateUserPreferences";
  bodyRM.exerciseLibrary = athlete.exerciseLibrary;

  bodyRM.exerciseLibrary[body.primary] = bodyRM.exerciseLibrary[
    body.primary
  ].map((elem) => {
    if (elem.movement === body.secondary) {
      change = true;
      return {
        movement: elem.movement,
        repMaximum: body.secondaryRepMaximum,
        customizedRM: true,
      };
    }
    return elem;
  });

  // if the secondary exercise doesnt exist in the exercise library, add it
  if (!change) {
    bodyRM.exerciseLibrary[body.primary].push({
      movement: body.secondary,
      repMaximum: body.secondaryRepMaximum,
      customizedRM: true,
    });
  }

  return bodyRM;
};

const RepMaximum = (app) => {
  app
    // Update from add exercise modal inside athlete-portal.
    .post(
      "/rep-maximum/update",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { body, query = {} } = req;
        const { proxyAthlete } = query;
        let bodyRM = {};

        await load("profileUser", { user: proxyAthlete });

        if (body.type === "primary") {
          assert(
            body.repMaximums,
            400,
            i18n.t("dashboard:repMaximumActions.provideRepMaximums")
          );
          assert(
            body.repMaximums[body.primary] > 0,
            400,
            i18n.t("dashboard:repMaximumActions.repMaxMustBeGreaterThanZero")
          );
          bodyRM = updatePrimaryRM(
            body,
            locals.profileUser,
            body.repMaximums[body.primary]
          );
        } else if (body.type === "secondary") {
          bodyRM = updateSecondaryRM(body, locals.profileUser);
        } else {
          return res.backDontScroll();
        }

        if (body.updateFutureRM) {
          bodyRM.action = "updateFutureRM";
          bodyRM.type = body.type;
          bodyRM.primary = body.primary;
          bodyRM.secondary = body.secondary;
          bodyRM.secondaryRepMaximum = body.secondaryRepMaximum;
        }

        await api(`user/${locals.profileUser._id}`, {
          method: "PUT",
          body: bodyRM,
        });
        flash(
          "success",
          i18n.t("dashboard:repMaximumActions.successfullyUpdated")
        );

        res.backDontScroll();
      }
    )
    .post(
      "/rep-maximum/update-primary",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { body, files, query = {} } = req;
        const { proxyAthlete } = query;
        const fromAddExercise = isLibraryMetadataFromAddExercise(body.source);
        let bodyRM = {};
        let user;
        assert(
          body.repMaximum,
          400,
          i18n.t("dashboard:repMaximumActions.provideRepMaximum")
        );
        assert(
          body.repMaximum > 0,
          400,
          i18n.t("dashboard:repMaximumActions.repMaxMustBeGreaterThanZero")
        );

        if (body.videoUrl?.length) {
          const isValidYoutube = youtubeParser(body.videoUrl);
          assert(
            isValidYoutube,
            400,
            i18n.t("dashboard:repMaximumActions.validYoutubeRequired")
          );
        }

        if (!proxyAthlete) {
          user = locals.activeUserToRequest;
        } else {
          await load("profileUser", { user: proxyAthlete });
          user = locals.profileUser;
        }

        const primaryExercises = get(user, "primaryExercises") || {};

        const otherExtraProperties = await otherExerciseProperties(
          body,
          files,
          api,
          assert,
          user,
          primaryExercises[body.primary]
        );

        primaryExercises[body.primary] = Object.assign(otherExtraProperties);

        set(user, "primaryExercises", primaryExercises);

        const userRM = user.repMaximums[body.primary];

        // Field is hidden for coaches and lb<->kg round-trips drift, so treat sub-rounding diffs as unchanged.
        const rmChanged =
          userRM == null ||
          Math.abs(Number(body.repMaximum) - Number(userRM)) > 0.01;

        bodyRM = updatePrimaryRM(
          body,
          user,
          rmChanged ? body.repMaximum : userRM
        );
        if (
          rmChanged &&
          confirm(i18n.t("dashboard:repMaximumActions.applyNewRmToUpcoming"))
        ) {
          bodyRM.action = "updateFutureRM";
          bodyRM.type = "primary";
          bodyRM.primary = body.primary;
        }

        delete body.source;

        await api(`user/${user._id}`, {
          method: "PUT",
          body: bodyRM,
        });

        api(`update-cycle-exercises-metadata`, {
          method: "PUT",
          body: { email: user.email, primary: body.primary },
        });

        if (fromAddExercise) {
          return respondLibraryMetadataFromAddExercise(
            res,
            otherExtraProperties
          );
        }

        flash(
          "success",
          i18n.t("dashboard:repMaximumActions.successfullyUpdated")
        );
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    );
};

export default RepMaximum;
